import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type {
  QualitativeThemePackage,
  QuickStudyAuthoredInstrument,
  QuickStudyResultsView,
  StudyFigureQuestion,
  StudyFigureThemeCards,
  StudyFindingsQuestion,
} from "@digital-twins/shared-types";
import { TrackBar } from "@digital-twins/deck-ui/components";
import { DECK_CHART_PRIMARY_COLOR } from "@digital-twins/deck-ui/foundations/chart-colors";
import { Text } from "@/components/ui/Text";
import { StudyDocQuotesGallery } from "@/components/study-doc/StudyDocQuotesGallery";
import type { StudyDocQuote } from "@/components/study-doc";
import { FigureSegmentControls } from "@/features/studies/report/figures/FigureSegmentControls";
import {
  FigureEntryStatus,
  QuestionFigureView,
} from "@/features/studies/report/figures/QuestionFigureView";
import { useQuestionCardFigures } from "@/features/studies/report/figures/useQuestionCardFigures";
import { formatStampedShare } from "@/features/studies/report/figures/views/figureViewShared";
import { ThemeCardFigure } from "@/features/studies/report/figures/views/ThemeCardFigure/ThemeCardFigure";
import { questionSupportsSegmentation } from "@/features/studies/report/page/components/views/analysis/QuestionCard";
import { getAppChartPalette } from "@/lib/charts/chartjs";
import { SavedQuestionFigure } from "./SavedQuestionFigure";
import { OpenEndQuoteGallery } from "@/features/studies/report/page/components/views/analysis/OpenEndQuoteGallery";
import { validatePlotAgainstJsonSchema } from "@/lib/validation/schema-loader";
import { buildBlurbLine, inferAudienceType } from "@/lib/utils/twin-blurb";
import type { QuickRunReportData } from "./useQuickRunReportData";

type Plot = StudyFigureQuestion["plots"][string];
type QualitativeThemes = QualitativeThemePackage["themes"];

const NO_QUESTIONS: Array<any> = [];
const NO_THEMES: QualitativeThemes = [];

/** One theme's label, its one-line description (when the package has one) and its share. */
function QuickStudyThemeHeader({
  title,
  description,
  percent,
}: {
  title: string;
  description?: string;
  percent?: number | null;
}) {
  const { i18n } = useTranslation("app");
  const line = (description ?? "").trim();
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <Text size="sm">{title}</Text>
        {line && line !== title.trim() ? (
          <Text
            size="xs"
            color="muted"
            className="mt-0.5 block"
            data-testid="quick-report-theme-description"
          >
            {line}
          </Text>
        ) : null}
      </div>
      {percent != null ? (
        <Text size="sm" weight="semibold" className="shrink-0">
          {formatStampedShare(percent, 1, i18n.language)}
        </Text>
      ) : null}
    </div>
  );
}

/** Theme shares of one open end, drawn as track bars. */
export function QuickStudyThemeBars({ themes }: { themes: QualitativeThemes }) {
  const { i18n } = useTranslation("app");
  return (
    <>
      {themes.map((theme) => (
        <div key={theme.id} className="space-y-1" data-testid="quick-report-theme-row">
          <QuickStudyThemeHeader
            title={theme.title}
            description={theme.description}
            percent={theme.percent}
          />
          <TrackBar
            widthPct={theme.percent}
            color={DECK_CHART_PRIMARY_COLOR}
            value={formatStampedShare(theme.percent, 1, i18n.language)}
            label={theme.title}
            withTooltip
          />
        </div>
      ))}
    </>
  );
}

/**
 * The figure service's stamped theme cards for the picked segment: Python's
 * shares and order, each joined to the package's words by theme id.
 */
function QuickStudyStampedThemes({
  cards,
  themes,
}: {
  cards: StudyFigureThemeCards;
  themes: QualitativeThemes;
}) {
  const wordsById = new Map(themes.map((theme) => [theme.id, theme]));
  const otherTitle =
    cards.themes
      .filter((card) => card.isOther)
      .map((card) => wordsById.get(card.themeId)?.title)
      .find(Boolean) ?? null;
  return (
    <>
      {cards.themes.map((card, index) => {
        const words = wordsById.get(card.themeId);
        if (!words) {
          return null;
        }
        return (
          <div key={card.themeId} className="space-y-1" data-testid="quick-report-theme-row">
            <QuickStudyThemeHeader title={words.title} description={words.description} />
            <ThemeCardFigure
              cards={cards}
              card={card}
              index={index}
              showsOverlapInfo={index === 0}
              otherTitle={otherTitle}
              displayBars
            />
          </div>
        );
      })}
    </>
  );
}

/** A SurveyJS question's helper text: a plain string or a localized `{ default }` map. */
export function questionHelperText(question: any): string {
  const value = question?.description;
  if (typeof value === "string") {
    return value.trim();
  }
  if (value && typeof value === "object") {
    const text = value.default ?? Object.values(value).find((entry) => typeof entry === "string");
    return typeof text === "string" ? text.trim() : "";
  }
  return "";
}

function isFreeTextQuestion(question: any): boolean {
  return question?.type === "text" || question?.type === "comment";
}

export interface QuickStudyReportQuoteEntry {
  id: string;
  quote: StudyDocQuote;
}

export interface QuickStudyReportQuestionPlotProps {
  question: StudyFindingsQuestion;
  qualitative?: QualitativeThemePackage;
  /**
   * The run's frozen survey (open document only): the helper text and the
   * segment control read it. Absent: the slot draws the saved figures alone.
   */
  run?: QuickRunReportData | null;
  /** This question's quotes the text does not already place (drawn under the plot). */
  quotes?: Array<QuickStudyReportQuoteEntry>;
}

/**
 * A13 document slot: one question drawn from the saved message (its
 * `nativePlot` and the snapshot's theme shares), no title (the document's
 * heading names the question). In the open document it adds the question's
 * helper text, the Survey data tab's segment control (the figure service's
 * cuts of the run, print uses the saved default) and the question's quotes
 * the text does not place.
 */
export function QuickStudyReportQuestionPlot(props: QuickStudyReportQuestionPlotProps) {
  const { question, qualitative, run = null, quotes } = props;

  const { t } = useTranslation("app");
  const surveyQuestion = run?.surveyQuestionFor(question.questionId) ?? null;
  const themes = qualitative?.themes ?? NO_THEMES;
  const isThemed =
    isFreeTextQuestion(surveyQuestion) &&
    themes.length > 0 &&
    themes.every((theme) => theme.quoteIds != null);
  const themeIds = useMemo(() => themes.map((theme) => theme.id), [themes]);
  const isSegmentable = questionSupportsSegmentation(surveyQuestion);

  const figures = useQuestionCardFigures({
    question: surveyQuestion,
    isEligible: surveyQuestion != null && (!isFreeTextQuestion(surveyQuestion) || isThemed),
    canSegment: isSegmentable || isThemed,
    segmentableQuestions: run?.segmentableQuestions ?? NO_QUESTIONS,
    canonicalQuestionMap: run?.canonicalQuestionMap,
    assetContextItems: run?.assetContextItems,
    assignedAssetLabel: t("studyReport.questionCard.segmentAssetShown"),
    allSubsegmentsLabel: t("studyReport.questionCard.segmentAllSubsegments"),
    isLaneMergedCard: false,
    defaultsToAsset: false,
    isThemed,
    themeIds,
  });

  const plot = question.nativePlot;
  const isValid = plot ? validatePlotAgainstJsonSchema(plot).length === 0 : false;
  const helperText = questionHelperText(surveyQuestion);
  const hasControls =
    figures.isFigureMode && (figures.dimensions.length > 0 || figures.metaStatus === "failed");
  const isSegmented = figures.isFigureMode && figures.activeDimension != null;

  const savedView = (
    <>
      {plot && isValid ? (
        <SavedQuestionFigure
          question={{ questionId: question.questionId, kind: plot.kind, plots: { total: plot } }}
          baseN={question.baseN}
          lowBase={question.lowBase}
          onRetry={() => undefined}
          oneDecimalShares
        />
      ) : null}
      {plot && !isValid ? (
        <Text size="sm" color="muted" role="alert">
          {t("threads.quickStudy.plotUnavailable")}
        </Text>
      ) : null}
      {themes.length ? <QuickStudyThemeBars themes={themes} /> : null}
    </>
  );

  let segmentedView = null;
  if (isSegmented && isThemed) {
    const entry = figures.entry;
    const cards =
      entry.status === "ready" && entry.question?.kind === "theme-cards"
        ? entry.question.themeCards ?? null
        : null;
    segmentedView = cards ? (
      <QuickStudyStampedThemes cards={cards} themes={themes} />
    ) : (
      <FigureEntryStatus
        entry={entry}
        onRetry={figures.retry}
        isNotCharted={figures.isNotChartedForView}
      />
    );
  } else if (isSegmented) {
    segmentedView = (
      <QuestionFigureView
        entry={figures.entry}
        isCut={figures.isCut}
        isNotCharted={figures.isNotChartedForView}
        colors={getAppChartPalette()}
        onRetry={figures.retry}
        controlsPlacement="below"
        viewport={{ maxHeight: 360 }}
        oneDecimalShares
      />
    );
  }

  return (
    <section className="space-y-4" data-testid="quick-report-question-plot">
      {helperText ? (
        <Text
          size="sm"
          color="muted"
          className="block"
          data-testid={`quick-report-question-helper-${question.questionId}`}
        >
          {helperText}
        </Text>
      ) : null}
      {hasControls ? (
        <div
          className="flex flex-wrap items-center gap-2 print:hidden"
          data-testid={`quick-report-question-segment-${question.questionId}`}
        >
          <FigureSegmentControls figures={figures} />
        </div>
      ) : null}
      {segmentedView ? (
        <>
          <div className="space-y-4 print:hidden" data-testid="quick-report-question-segmented">
            {segmentedView}
          </div>
          {/* Print and PDF keep the default segment. */}
          <div className="hidden space-y-4 print:block">{savedView}</div>
        </>
      ) : (
        savedView
      )}
      {quotes && quotes.length > 0 ? (
        <div data-testid={`quick-report-question-quotes-${question.questionId}`}>
          <StudyDocQuotesGallery entries={quotes} />
        </div>
      ) : null}
    </section>
  );
}
export function QuickStudyResultsQuestions({
  evidence,
  questions,
  onRetry,
}: {
  evidence: QuickStudyResultsView;
  questions: QuickStudyAuthoredInstrument["questions"];
  onRetry: () => void;
}) {
  const { t } = useTranslation("app");
  return (
    <>
      {questions.map((question) => {
        const saved = evidence.figures.questions.find(
          (figure) => figure.questionId === question.id,
        );
        const plots = Object.fromEntries(
          Object.entries(saved?.plots ?? {}).filter(
            (entry): entry is [string, Plot] =>
              validatePlotAgainstJsonSchema(entry[1]).length === 0,
          ),
        );
        const total = evidence.figures.groups.find(
          (group) => group.kind === "total",
        );
        const coverage = evidence.responseCoverage.questions[question.id];
        const qualitative = Object.values(evidence.qualitativeResults).find(
          (pack) => pack.questionId === question.id,
        );
        return (
          <section
            key={question.id}
            className="space-y-4"
            data-testid="quick-saved-question-evidence"
          >
            <div className="flex items-start justify-between gap-3">
              <Text role="heading" aria-level={3} size="base" weight="semibold">
                {question.title}
              </Text>
              {coverage ? (
                <Text size="xs" color="muted" className="shrink-0">
                  n={coverage.answered}
                </Text>
              ) : null}
            </div>
            {saved && Object.keys(plots).length > 0 && total ? (
              <SavedQuestionFigure
                question={{ ...saved, plots }}
                groups={[
                  {
                    ...total,
                    label: total.label ?? null,
                    kind: "total",
                    sourceId: null,
                    drawn: true,
                  },
                ]}
                baseN={coverage?.answered}
                lowBase={total.lowBase}
                onRetry={onRetry}
                oneDecimalShares
              />
            ) : null}
            {saved &&
            Object.keys(saved.plots).length > Object.keys(plots).length ? (
              <Text size="sm" color="muted" role="alert">
                {t("threads.quickStudy.plotUnavailable")}
              </Text>
            ) : null}
            {qualitative ? (
              <>
                <QuickStudyThemeBars themes={qualitative.themes} />
                <OpenEndQuoteGallery
                  themePackage={qualitative}
                  versions={[]}
                  hasActiveFigureFilter={false}
                  speakerFor={(quote) => {
                    const speaker =
                      quote.respondentIdentityVerified !== false &&
                      quote.respondentId
                        ? evidence.quoteSpeakers?.[quote.respondentId]
                        : undefined;
                    return speaker
                      ? {
                          name: speaker.name,
                          blurb:
                            buildBlurbLine(
                              speaker.displayProfile,
                              inferAudienceType(speaker.displayProfile),
                            ) || undefined,
                          segmentLabels: speaker.segmentLabels,
                        }
                      : null;
                  }}
                  initialsFor={(name) =>
                    name
                      .trim()
                      .split(/\s+/)
                      .map((part) => part[0])
                      .slice(0, 2)
                      .join("")
                  }
                />
              </>
            ) : null}
          </section>
        );
      })}
    </>
  );
}
